import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
	isEmailNotVerifiedError,
	useGoogleLogin,
	useLogin,
} from "@bodhistreams/queries/src/useAuthQueries";
import useUIStore from "@bodhistreams/stores/src/uiStore";
import { useGoogleLogin as useGoogleOAuth } from "@react-oauth/google";
import { Alert, Button, Form, Input, theme } from "antd";
import { useEffect, useState } from "react";
import Logo from "../../Layout/Logo";
import EmailNotVerified from "../EmailNotVerified";

const { useToken } = theme;

const injectLoginStyles = (styleId, primary, success) => {
	if (document.getElementById(styleId)) return;
	const style = document.createElement("style");
	style.id = styleId;
	style.innerHTML = `
		@keyframes authFadeUp {
			from { opacity: 0; transform: translateY(18px); }
			to   { opacity: 1; transform: translateY(0); }
		}
		@keyframes logoPulse {
			0%, 100% { box-shadow: 0 0 0 0 ${primary}33; }
			50%       { box-shadow: 0 0 0 12px ${primary}00; }
		}
		.auth-glass-card {
			animation: authFadeUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
			width: 420px;
			max-width: 95vw;
			border-radius: 24px;
			background: rgba(20, 28, 24, 0.72);
			backdrop-filter: blur(28px) saturate(1.4);
			-webkit-backdrop-filter: blur(28px) saturate(1.4);
			border: 1px solid rgba(107, 158, 141, 0.18);
			box-shadow:
				0 8px 32px rgba(0, 0, 0, 0.45),
				0 1px 0 rgba(107, 158, 141, 0.12) inset,
				0 -1px 0 rgba(0, 0, 0, 0.3) inset;
			overflow: hidden;
		}
		.auth-logo-ring {
			width: 72px; height: 72px;
			border-radius: 50%;
			background: radial-gradient(circle at 40% 35%, rgba(107,158,141,0.22) 0%, rgba(20,28,24,0.6) 70%);
			border: 1px solid rgba(107,158,141,0.28);
			display: flex; align-items: center; justify-content: center;
			animation: logoPulse 3.5s ease-in-out infinite;
			box-shadow: 0 4px 20px rgba(107,158,141,0.15);
		}
		.auth-header {
			padding: 36px 40px 0;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 16px;
		}
		.auth-title {
			margin: 0;
			font-size: 22px;
			font-weight: 600;
			letter-spacing: -0.3px;
			color: #E8E5E0;
		}
		.auth-subtitle {
			margin: 2px 0 0;
			font-size: 13px;
			color: rgba(184,181,176,0.7);
			letter-spacing: 0.2px;
		}
		.auth-body { padding: 28px 40px 36px; }
		.auth-divider {
			display: flex;
			align-items: center;
			gap: 12px;
			margin: 20px 0;
			color: rgba(184,181,176,0.4);
			font-size: 12px;
			letter-spacing: 0.5px;
			text-transform: uppercase;
		}
		.auth-divider::before,
		.auth-divider::after {
			content: '';
			flex: 1;
			height: 1px;
			background: rgba(107,158,141,0.12);
		}
		.auth-footer {
			text-align: center;
			margin-top: 22px;
			font-size: 13px;
			color: rgba(184,181,176,0.65);
		}
		.auth-forgot {
			display: flex;
			justify-content: flex-end;
			margin: -2px 0 18px;
		}
		/* Glass inputs */
		.auth-glass-card .ant-input-affix-wrapper {
			background: rgba(255,255,255,0.04) !important;
			border: 1px solid rgba(107,158,141,0.2) !important;
			border-radius: 12px !important;
			height: 46px;
			transition: border-color 0.2s, box-shadow 0.2s;
		}
		.auth-glass-card .ant-input-affix-wrapper:hover {
			border-color: rgba(107,158,141,0.4) !important;
		}
		.auth-glass-card .ant-input-affix-wrapper-focused,
		.auth-glass-card .ant-input-affix-wrapper:focus-within {
			border-color: ${primary} !important;
			box-shadow: 0 0 0 3px ${primary}22 !important;
			background: rgba(107,158,141,0.06) !important;
		}
		.auth-glass-card .ant-input {
			background: transparent !important;
			color: #E8E5E0 !important;
			font-size: 14px;
		}
		.auth-glass-card .ant-input::placeholder { color: rgba(184,181,176,0.45) !important; }
		.auth-glass-card .ant-input-prefix { color: rgba(107,158,141,0.7) !important; margin-right: 10px; }
		.auth-glass-card .ant-input-suffix .anticon { color: rgba(184,181,176,0.4) !important; }
		/* Autofill */
		.auth-glass-card input:-webkit-autofill,
		.auth-glass-card input:-webkit-autofill:hover,
		.auth-glass-card input:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px rgba(20,28,24,0.95) inset !important;
			-webkit-text-fill-color: #E8E5E0 !important;
			transition: background-color 5000s ease-in-out 0s;
		}
		/* Submit button */
		.auth-submit-btn {
			width: 100%;
			height: 46px;
			border-radius: 12px !important;
			background: linear-gradient(135deg, ${primary} 0%, ${success} 100%) !important;
			border: none !important;
			font-size: 14px;
			font-weight: 600;
			letter-spacing: 0.3px;
			color: #fff !important;
			box-shadow: 0 4px 16px ${primary}44 !important;
			transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s !important;
		}
		.auth-submit-btn:hover:not(:disabled) {
			opacity: 0.88 !important;
			transform: translateY(-1px);
			box-shadow: 0 6px 20px ${primary}55 !important;
		}
		.auth-submit-btn:active:not(:disabled) { transform: translateY(0); }
		/* Google button */
		.auth-google-btn {
			width: 100%;
			height: 46px;
			border-radius: 12px !important;
			background: rgba(255,255,255,0.04) !important;
			border: 1px solid rgba(107,158,141,0.2) !important;
			color: #E8E5E0 !important;
			font-size: 14px;
			font-weight: 500;
			display: flex !important;
			align-items: center;
			justify-content: center;
			gap: 8px;
			transition: background 0.2s, border-color 0.2s !important;
		}
		.auth-google-btn:hover:not(:disabled) {
			background: rgba(107,158,141,0.1) !important;
			border-color: rgba(107,158,141,0.4) !important;
		}
		.auth-google-btn .anticon { color: #E8E5E0 !important; }
		/* Form items */
		.auth-glass-card .ant-form-item { margin-bottom: 14px; }
		.auth-glass-card .ant-form-item:last-child { margin-bottom: 0; }
		.auth-glass-card .ant-form-item-explain-error { font-size: 12px; padding-left: 4px; }
		/* Alert */
		.auth-glass-card .ant-alert {
			background: rgba(212,132,122,0.12) !important;
			border: 1px solid rgba(212,132,122,0.3) !important;
			border-radius: 10px !important;
			margin-bottom: 16px;
		}
		/* Modal overlay */
		.auth-modal-overlay {
			position: fixed;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 24px;
			z-index: 1000;
			background:
				radial-gradient(circle at top, rgba(107, 158, 141, 0.12), transparent 32%),
				rgba(8, 12, 10, 0.6);
			backdrop-filter: blur(12px);
			-webkit-backdrop-filter: blur(12px);
		}
	`;
	document.head.appendChild(style);
};

const GoogleIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
	>
		<path
			d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			fill="#4285F4"
		/>
		<path
			d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			fill="#34A853"
		/>
		<path
			d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
			fill="#FBBC05"
		/>
		<path
			d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			fill="#EA4335"
		/>
	</svg>
);

const CustomModal = ({ isOpen, onClose, children }) => {
	useEffect(() => {
		if (!isOpen) return undefined;

		document.body.style.overflow = "hidden";

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = "auto";
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			className="auth-modal-overlay"
			onMouseDown={onClose}
			role="dialog"
			aria-modal="true"
		>
			<div
				className="auth-glass-card"
				onMouseDown={(event) => event.stopPropagation()}
				role="document"
			>
				{children}
			</div>
		</div>
	);
};

const LoginModal = () => {
	const { token } = useToken();
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	// Set when the server refuses a correct password because the email is
	// unverified; swaps the form for the verification notice.
	const [unverifiedEmail, setUnverifiedEmail] = useState(null);
	const {
		isLoginModalVisible,
		hideLoginModal,
		switchToRegister,
		switchToReset,
	} = useUIStore();
	const { mutateAsync: loginMutation } = useLogin();
	const { mutateAsync: googleLoginMutation } = useGoogleLogin();
	const [form] = Form.useForm();

	const googleOAuth = useGoogleOAuth({
		flow: "auth-code",
		onSuccess: async (codeResponse) => {
			try {
				await googleLoginMutation(codeResponse.code);
			} catch (_) {
				// error handled by mutation onError
			} finally {
				setGoogleLoading(false);
			}
		},
		onError: () => {
			setGoogleLoading(false);
			setError("Google login failed. Please try again.");
		},
	});

	useEffect(() => {
		injectLoginStyles(
			"auth-glass-styles",
			token.colorPrimary,
			token.colorSuccess,
		);
	}, [token.colorPrimary, token.colorSuccess]);

	const handleGoogleLogin = () => {
		setGoogleLoading(true);
		googleOAuth();
	};

	const handleCancel = () => {
		setError(null);
		setUnverifiedEmail(null);
		hideLoginModal();
	};

	const onFinish = async (values) => {
		setError(null);
		setLoading(true);
		try {
			await loginMutation(values);
			form.resetFields();
		} catch (err) {
			if (isEmailNotVerifiedError(err)) {
				setUnverifiedEmail(err.response.data.email || values.email);
			} else {
				setError(
					err.response?.data?.message || err.message || "An error occurred",
				);
			}
		}
		setLoading(false);
	};

	if (unverifiedEmail) {
		return (
			<CustomModal isOpen={isLoginModalVisible} onClose={handleCancel}>
				<div className="auth-body" style={{ paddingTop: 36 }}>
					<EmailNotVerified
						email={unverifiedEmail}
						onBack={() => setUnverifiedEmail(null)}
					/>
				</div>
			</CustomModal>
		);
	}

	return (
		<CustomModal isOpen={isLoginModalVisible} onClose={handleCancel}>
			<div className="auth-header">
				<div className="auth-logo-ring">
					<Logo.Favicon size={38} hyperlink={true} />
				</div>
				<div style={{ textAlign: "center" }}>
					<h2 className="auth-title">Welcome back</h2>
					<p className="auth-subtitle">Sign in to {Logo.APP_NAME}</p>
				</div>
			</div>

			<div className="auth-body">
				{error && <Alert message={error} type="error" showIcon />}

				<Form form={form} name="login" onFinish={onFinish}>
					<Form.Item
						name="email"
						rules={[{ required: true, message: "Please input your email!" }]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Email address"
							autoComplete="email"
						/>
					</Form.Item>
					<Form.Item
						name="password"
						rules={[{ required: true, message: "Please input your password!" }]}
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Password"
							autoComplete="current-password"
						/>
					</Form.Item>

					<div className="auth-forgot">
						<Button
							type="link"
							onClick={switchToReset}
							style={{
								padding: 0,
								height: "auto",
								fontSize: 13,
								color: token.colorPrimary,
							}}
						>
							Forgot password?
						</Button>
					</div>

					<Form.Item style={{ marginTop: 20, marginBottom: 0 }}>
						<Button
							type="primary"
							htmlType="submit"
							className="auth-submit-btn"
							loading={loading}
						>
							Sign in
						</Button>
					</Form.Item>
				</Form>

				<div className="auth-divider">or</div>

				<Button
					className="auth-google-btn"
					onClick={handleGoogleLogin}
					loading={googleLoading}
					disabled={loading}
					icon={<GoogleIcon />}
				>
					Continue with Google
				</Button>

				<div className="auth-footer">
					Don&apos;t have an account?{" "}
					<Button
						type="link"
						onClick={switchToRegister}
						style={{
							padding: 0,
							height: "auto",
							fontSize: 13,
							color: token.colorPrimary,
						}}
					>
						Create one
					</Button>
				</div>
			</div>
		</CustomModal>
	);
};

export default LoginModal;
